<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="mm-question">
        <span class="mm-bar mm-bar--xl"></span>
      </div>
      <div class="mm-canvas">
        <svg viewBox="0 0 220 130" class="mm-svg">
          <!-- branches -->
          <path
            d="M110 65 Q70 35 35 25"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            opacity="0.4"
          />
          <path
            d="M110 65 Q160 30 195 22"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            opacity="0.4"
          />
          <path
            d="M110 65 Q70 95 30 110"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            opacity="0.4"
          />
          <path
            d="M110 65 Q160 100 200 110"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            opacity="0.4"
          />
          <!-- new growing branch -->
          <path
            class="mm-grow-line"
            d="M110 65 Q140 50 170 54"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
          />
        </svg>
        <div class="mm-node mm-node--center">
          <span>Future of work</span>
        </div>
        <div class="mm-node mm-node--child mm-node--tl">
          Remote
        </div>
        <div class="mm-node mm-node--child mm-node--tr">
          AI tools
        </div>
        <div class="mm-node mm-node--child mm-node--bl">
          Async
        </div>
        <div class="mm-node mm-node--child mm-node--br">
          Wellbeing
        </div>
        <div class="mm-node mm-node--child mm-node--new">
          4-day week
        </div>
      </div>
    </template>

    <template #audience>
      <span class="mm-phone-q"></span>
      <div class="mm-phone-selected">
        <span class="material-symbols-rounded">hub</span>
        Future of work
      </div>
      <div class="mm-phone-input">
        <span class="mm-phone-typed">4-day week<span class="mm-caret">|</span></span>
      </div>
      <button type="button" class="mm-phone-add" tabindex="-1">
        <span class="material-symbols-rounded">add</span>
        Add branch
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewMindMap',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
})
</script>

<style scoped lang="scss">
.mm-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.mm-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.mm-canvas {
  position: relative;
  flex: 1;
  min-height: 130px;
  color: var(--accent);
}
.mm-svg {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
}
.mm-grow-line {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: mm-grow 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

.mm-node {
  position: absolute;
  background: white;
  border: 1.5px solid color-mix(in srgb, var(--accent) 40%, transparent);
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 9px; font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(15, 15, 20, 0.08);
}
.mm-node--center {
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  background: var(--accent);
  color: white;
  border-color: var(--accent);
  font-size: 10px;
  padding: 6px 12px;
}
.mm-node--child {
  color: var(--accent);
}
.mm-node--tl { top: 8%;  left: 4%; }
.mm-node--tr { top: 6%;  right: 2%; }
.mm-node--bl { bottom: 6%; left: 4%; }
.mm-node--br { bottom: 6%; right: 4%; }
.mm-node--new {
  top: 32%; right: 12%;
  background: color-mix(in srgb, var(--accent) 15%, white);
  animation: mm-pop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.mm-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.mm-phone-selected {
  display: flex; align-items: center; gap: 4px;
  padding: 7px 9px;
  background: var(--accent); color: white;
  border-radius: 999px;
  font-size: 11px; font-weight: 700;
  align-self: start;
  width: max-content;
  max-width: 100%;
}
.mm-phone-selected .material-symbols-rounded { font-size: 14px; }
.mm-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 9px 9px;
  background: white;
}
.mm-phone-typed {
  font-size: 12px; font-weight: 600;
  color: rgba(15, 15, 20, 0.85);
}
.mm-caret { color: var(--accent); animation: mm-blink 1s steps(2) infinite; }
.mm-phone-add {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: mm-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.mm-phone-add .material-symbols-rounded { font-size: 14px; }

@keyframes mm-grow {
  0%, 15% { stroke-dashoffset: 100; }
  45%     { stroke-dashoffset: 0; }
  92%, 100% { stroke-dashoffset: 0; }
}
@keyframes mm-pop {
  0%, 25% { transform: scale(0); opacity: 0; }
  45%     { transform: scale(1.15); opacity: 1; }
  60%, 92% { transform: scale(1); opacity: 1; }
  100%    { transform: scale(1); opacity: 1; }
}
@keyframes mm-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes mm-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .mm-grow-line, .mm-node--new, .mm-caret, .mm-phone-add { animation: none; }
  .mm-grow-line { stroke-dashoffset: 0; }
  .mm-node--new { transform: scale(1); opacity: 1; }
}
</style>
